import LanTabs from './lan/LanTabs';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertModal, ConfirmModal } from './AlertModal';
import Pagination from './Pagination';
import { cacheGet, cacheSet } from '../utils/clientCache';
import ServerTable, { isLanServer } from './servers/ServerTable';
import LocationModal from './servers/LocationModal';
import ServerPanel from './servers/ServerPanel';
import ServerConsole from './servers/ServerConsole';
import { SvIcon } from './servers/svIcons';
import './servers/servers.css';

const EMPTY_FORM = {
  name: '', ip: '', port: '', rcon_password: '',
  backup_protocol: 'sftp', backup_host: '', backup_port: '',
  backup_user: '', backup_password: '', backup_remote_path: '',
  linuxgsm_enabled: false, linuxgsm_script: 'cs2server',
  public_ip: '', show_sdr_after_veto: false,
};
const STATUS_TONE = { all: 'var(--sv-blue)', available: 'var(--sv-green)', busy: 'var(--sv-amber)', offline: 'var(--sv-red)' };

function Servers() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Last list from this tab's cache → the page renders instantly, then refreshes in the background
  const cachedList = cacheGet('servers:list:all:1:10');
  const [servers, setServers] = useState(cachedList ? cachedList.servers : []);
  const [summary, setSummary] = useState(cachedList ? cachedList.summary : null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(cachedList ? cachedList.totalPages : 1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(!cachedList);
  const [searchQuery, setSearchQuery] = useState('');

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [backupTestSuccess, setBackupTestSuccess] = useState(false);
  const [uploadingConfig, setUploadingConfig] = useState(null);
  // Which connection test is running: null | 'modal-rcon' | 'modal-backup' | `rcon-<id>` | `backup-<id>`
  const [testingConn, setTestingConn] = useState(null);

  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [consoleFor, setConsoleFor] = useState(null); // { server, lgsmAction? }

  const alert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const confirm = (message, onConfirm) => setConfirmModal({
    show: true, message,
    onConfirm: async () => { setConfirmModal({ show: false, message: '', onConfirm: null }); await onConfirm(); },
  });

  // ── list ──
  const listArgs = useRef({});
  listArgs.current = { page, pageSize, statusFilter, searchQuery };
  const fetchServers = useCallback(async () => {
    const { page: p, pageSize: size, statusFilter: st, searchQuery: q } = listArgs.current;
    const all = !!q || size === 'all';
    const limit = all ? 10000 : size;
    const key = `servers:list:${st}:${all ? 1 : p}:${size}`;
    try {
      const res = await axios.get(`/api/servers?page=${all ? 1 : p}&limit=${limit}${st !== 'all' ? `&status=${st}` : ''}`);
      const list = res.data.data || res.data;
      const pages = all ? 1 : (res.data.pagination ? res.data.pagination.totalPages : 1);
      setServers(list);
      setTotalPages(pages || 1);
      if (res.data.summary) setSummary(res.data.summary);
      if (!q) cacheSet(key, { servers: list, totalPages: pages, summary: res.data.summary });
    } catch (_) {
      // a failed background refresh keeps the last list on screen
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      const cached = cacheGet(`servers:list:${statusFilter}:${page}:${pageSize}`);
      if (cached) { setServers(cached.servers); setTotalPages(cached.totalPages); if (cached.summary) setSummary(cached.summary); setLoading(false); }
    }
    fetchServers();
    // background refresh every 10 s, but not while the tab is hidden
    const interval = setInterval(() => { if (!document.hidden) fetchServers(); }, 10000);
    return () => clearInterval(interval);
  }, [page, pageSize, statusFilter, searchQuery, fetchServers]);

  const visibleServers = servers.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.ip || '').toLowerCase().includes(q) || (s.public_ip || '').toLowerCase().includes(q);
  });
  const visibleSelected = visibleServers.filter(s => selectedIds.has(s.id));

  // ── form ──
  const openAdd = () => {
    setEditingServer(null);
    setFormData(EMPTY_FORM);
    setBackupTestSuccess(false);
    setPanelOpen(true);
  };
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const handleEdit = async (server) => {
    try {
      const { data: s } = await axios.get(`/api/servers/${server.id}/edit`);
      setEditingServer(s);
      setFormData({
        name: s.name, ip: s.ip, port: s.port, rcon_password: s.rcon_password || '',
        backup_protocol: s.backup_protocol || 'sftp', backup_host: s.backup_host || '', backup_port: s.backup_port || '',
        backup_user: s.backup_user || '', backup_password: s.backup_password || '', backup_remote_path: s.backup_remote_path || '',
        linuxgsm_enabled: !!s.linuxgsm_enabled, linuxgsm_script: s.linuxgsm_script || 'cs2server',
        public_ip: s.public_ip || '', show_sdr_after_veto: !!s.show_sdr_after_veto,
      });
      setBackupTestSuccess(false);
      setPanelOpen(true);
    } catch (error) {
      alert(error.response?.data?.error || t('servers.loadFailed', 'Failed to load server data'), 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingServer) {
        await axios.put(`/api/servers/${editingServer.id}`, formData);
        alert(t('servers.updated', 'Server updated successfully'), 'success');
      } else {
        await axios.post('/api/servers', formData);
        alert(t('servers.added', 'Server added successfully'), 'success');
      }
      setPanelOpen(false);
      setEditingServer(null);
      fetchServers();
    } catch (error) {
      alert(error.response?.data?.error || t('servers.saveFailed', 'Failed to save server'), 'error');
    }
  };

  // ── connection tests ──
  const backupResultMsg = (pathValid) => pathValid
    ? [t('servers.backupOkPath', 'SFTP/FTP connection successful:\nGame server path is correct'), 'success']
    : [t('servers.backupBadPath', 'SFTP/FTP connection successful:\nGame server path is incorrect'), 'warning'];
  const backupFailedMsg = t('servers.backupFailed', 'SFTP/FTP connection failed:\nDetails wrong or not provided');

  const testRcon = async (server) => {
    setTestingConn(`rcon-${server.id}`);
    try {
      const res = await axios.post(`/api/servers/${server.id}/test`);
      alert(`${t('servers.rconOk', 'RCON connection successful:')}\n${res.data.response || ''}`, 'success');
    } catch (error) {
      alert(`${t('servers.rconFailed', 'RCON connection failed:')} ${error.response?.data?.error || 'Unknown error'}`, 'error');
    } finally { setTestingConn(null); }
  };

  const testBackup = async (server) => {
    setTestingConn(`backup-${server.id}`);
    try {
      const res = await axios.post(`/api/servers/${server.id}/test-backup`);
      alert(...backupResultMsg(res.data.pathValid));
    } catch (_) {
      alert(backupFailedMsg, 'error');
    } finally { setTestingConn(null); }
  };

  const testRconFromForm = async () => {
    setTestingConn('modal-rcon');
    try {
      const res = await axios.post('/api/servers/test-rcon-connection', { ip: formData.ip, port: formData.port, rcon_password: formData.rcon_password });
      alert(`${t('servers.rconOk', 'RCON connection successful:')}\n${res.data.response || ''}`, 'success');
    } catch (error) {
      alert(`${t('servers.rconFailed', 'RCON connection failed:')} ${error.response?.data?.error || error.message}`, 'error');
    } finally { setTestingConn(null); }
  };

  const testBackupFromForm = async () => {
    if (!formData.backup_host && !formData.ip) { setBackupTestSuccess(false); return alert(backupFailedMsg, 'error'); }
    setTestingConn('modal-backup');
    try {
      const res = await axios.post('/api/servers/test-backup-connection', formData);
      setBackupTestSuccess(!!res.data.pathValid);
      alert(...backupResultMsg(res.data.pathValid));
    } catch (_) {
      setBackupTestSuccess(false);
      alert(backupFailedMsg, 'error');
    } finally { setTestingConn(null); }
  };

  // ── row actions ──
  const uploadConfig = (server) => confirm(
    t('servers.uploadConfigConfirm', 'Upload the config files (warmup.cfg, knife.cfg, live.cfg) to this game server? Existing files are replaced.'),
    async () => {
      setUploadingConfig(server.id);
      try {
        const res = await axios.post(`/api/servers/${server.id}/upload-configs`);
        alert(res.data.message, 'success');
      } catch (error) {
        alert(error.response?.data?.error || t('servers.uploadConfigFailed', 'Failed to upload config files'), 'error');
      } finally { setUploadingConfig(null); }
    });

  // Set location (servers/LocationModal.js): pick by hand, or detect from the IP (not LAN servers)
  const [locationFor, setLocationFor] = useState(null);
  const setLocation = (server) => setLocationFor(server);

  const forceFree = (server) => confirm(
    t('servers.forceFreeConfirm', 'Force this server to available? Only do this if it is stuck busy with no active match - freeing a server that is genuinely in use can disrupt a live match.'),
    async () => {
      try {
        await axios.post(`/api/servers/${server.id}/force-free`);
        alert(t('servers.forceFreeSuccess', 'Server freed'), 'success');
        fetchServers();
      } catch (error) {
        alert(error.response?.data?.error || t('servers.forceFreeError', 'Failed to free server'), 'error');
      }
    });

  const remove = (server) => confirm(
    t('servers.deleteConfirm', 'Delete server "{{name}}"?', { name: server.name }),
    async () => {
      try {
        await axios.delete(`/api/servers/${server.id}`);
        alert(t('servers.deleted', 'Server deleted'), 'success');
        setSelectedIds(prev => { const n = new Set(prev); n.delete(server.id); return n; });
        fetchServers();
      } catch (error) {
        alert(error.response?.data?.error || t('servers.deleteFailed', 'Failed to delete server'), 'error');
      }
    });

  const LGSM_CONFIRM = {
    start: t('servers.lgsmStartConfirm', 'Start this server?'),
    restart: t('servers.lgsmRestartConfirm', 'Restart this server? Players will be disconnected.'),
    stop: t('servers.lgsmStopConfirm', 'Stop this server? All players are kicked and a live match may be interrupted.'),
    update: t('servers.lgsmUpdateConfirm', 'Update this server? It goes offline during the update.'),
    'force-update': t('servers.lgsmForceUpdateConfirm', 'Force an update? SteamCMD downloads the game update even if the server is current; the server goes offline meanwhile.'),
    validate: t('servers.lgsmValidateConfirm', 'Validate the game files? The server goes offline while SteamCMD checks every file (can take several minutes).'),
    backup: t('servers.lgsmBackupConfirm', 'Back up the whole server? LinuxGSM may stop the server during the backup and it needs free disk space.'),
  };
  // monitor, details and update-lgsm don't affect players: no confirmation
  const lgsm = (server, action) => {
    const open = () => setConsoleFor({ server, lgsmAction: action });
    if (LGSM_CONFIRM[action]) confirm(LGSM_CONFIRM[action], open); else open();
  };

  const actions = {
    console: (server) => setConsoleFor({ server }),
    files: (server) => navigate(`/admin/servers/${server.id}/files`),
    testRcon, testBackup, setLocation, uploadConfig, forceFree, lgsm,
    edit: handleEdit, remove, uploadingConfig,
  };

  // ── selection ──
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelectedIds(prev => {
    const n = new Set(prev);
    const allOn = visibleServers.length > 0 && visibleServers.every(s => n.has(s.id));
    visibleServers.forEach(s => (allOn ? n.delete(s.id) : n.add(s.id)));
    return n;
  });
  const bulkDelete = () => {
    const ids = visibleSelected.map(s => s.id);
    if (!ids.length) return;
    confirm(t('servers.bulkDeleteConfirm', 'Delete {{count}} selected servers? This cannot be undone.', { count: ids.length }), async () => {
      setBulkDeleting(true);
      const results = await Promise.allSettled(ids.map(id => axios.delete(`/api/servers/${id}`)));
      const failed = results.filter(r => r.status === 'rejected').length;
      setBulkDeleting(false);
      setSelectedIds(new Set());
      await fetchServers();
      if (failed > 0) {
        alert(t('servers.bulkDeletePartial', 'Deleted {{ok}} of {{total}} servers. {{failed}} failed.', { ok: ids.length - failed, total: ids.length, failed }), failed === ids.length ? 'error' : 'warning');
      } else {
        alert(t('servers.bulkDeleteSuccess', 'Deleted {{count}} servers', { count: ids.length }), 'success');
      }
    });
  };

  const setFilter = (st) => { setStatusFilter(st); setPage(1); };
  const stat = (key, label, value, tone) => (
    <button type="button" key={key} className={`sv-stat${statusFilter === key ? ' on' : ''}`} style={{ '--sv-tone': tone }} aria-pressed={statusFilter === key} onClick={() => setFilter(key)}>
      <span className="sv-stat-label">{label}</span>
      <span className="sv-stat-value">{summary ? value : '–'}</span>
    </button>
  );

  const emptyText = searchQuery
    ? t('servers.noSearchResults', 'No servers match your search.')
    : statusFilter !== 'all'
      ? t('servers.noStatusResults', 'No servers with this status.')
      : t('servers.noServers', 'No servers configured.');

  return (
    <div className="sv-page">
      <LanTabs active="servers" />

      <div className="sv-head">
        <h1 className="sv-title">{t('nav.servers', 'Servers')}</h1>
        <span className="sv-head-meta">{t('servers.statPlayers', 'Players online')} <b>{summary ? summary.players : '–'}</b></span>
        <span className="sv-spacer" />
        <button type="button" className="sv-btn primary" onClick={openAdd}><SvIcon name="plus" size={14} />{t('servers.addServer', 'Add Server')}</button>
      </div>

      {/* status filter: words with counts in one line (was five stat cards) */}
      <div className="sv-stats" role="group" aria-label={t('servers.colStatus', 'Status')}>
        {stat('all', t('servers.statTotal', 'All servers'), summary?.total, STATUS_TONE.all)}
        {stat('available', t('servers.statusAvailable', 'Available'), summary?.available, STATUS_TONE.available)}
        {stat('busy', t('servers.statusBusy', 'Busy'), summary?.busy, STATUS_TONE.busy)}
        {stat('offline', t('servers.statusOffline', 'Offline'), summary?.offline, STATUS_TONE.offline)}
      </div>

      <div className="sv-toolbar">
        <div className="sv-search">
          <SvIcon name="search" size={14} />
          <input placeholder={t('servers.searchPlaceholder', 'Search servers...')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          {searchQuery && <button type="button" className="sv-clear" onClick={() => setSearchQuery('')} aria-label={t('common.clear', 'Clear')}><SvIcon name="close" size={13} /></button>}
        </div>
        <span className="sv-spacer" />
        <span className="sv-muted">{t('servers.perPage', 'Per page')}</span>
        <div className="sv-seg">
          {[10, 50, 'all'].map(size => (
            <button key={size} type="button" className={pageSize === size ? 'on' : ''} onClick={() => { setPageSize(size); setPage(1); }}>
              {size === 'all' ? t('common.all', 'All') : size}
            </button>
          ))}
        </div>
      </div>

      {visibleSelected.length > 0 && (
        <div className="sv-selbar">
          <strong>{t('servers.selectedCount', '{{count}} selected', { count: visibleSelected.length })}</strong>
          <button type="button" className="sv-btn small" onClick={() => setSelectedIds(new Set())}>{t('common.clear', 'Clear')}</button>
          <span className="sv-spacer" />
          <button type="button" className="sv-btn small danger" onClick={bulkDelete} disabled={bulkDeleting}>
            {bulkDeleting ? <span className="sv-spin" /> : <SvIcon name="trash" size={13} />}
            {t('servers.deleteSelected', 'Delete Selected ({{count}})', { count: visibleSelected.length })}
          </button>
        </div>
      )}

      <ServerTable
        servers={visibleServers}
        loading={loading}
        selectedIds={selectedIds}
        onToggle={toggleSelect}
        onToggleAll={toggleAll}
        actions={actions}
        navigate={navigate}
        emptyText={emptyText}
        onAdd={!searchQuery && statusFilter === 'all' ? openAdd : null}
      />

      {!searchQuery && totalPages > 1 && (
        <div className="sv-pager"><Pagination page={page} totalPages={totalPages} onPageChange={setPage} /></div>
      )}

      <ServerPanel
        open={panelOpen}
        editing={editingServer}
        formData={formData}
        setFormData={setFormData}
        onClose={closePanel}
        onSubmit={handleSubmit}
        onTestRcon={testRconFromForm}
        onTestBackup={testBackupFromForm}
        onUploadConfig={() => editingServer && uploadConfig(editingServer)}
        testingConn={testingConn}
        backupTestSuccess={backupTestSuccess}
        uploadingConfig={uploadingConfig}
      />

      {consoleFor && (
        <ServerConsole
          key={`${consoleFor.server.id}:${consoleFor.lgsmAction || ''}`}
          server={consoleFor.server}
          lgsmAction={consoleFor.lgsmAction}
          onClose={() => setConsoleFor(null)}
          onDone={fetchServers}
        />
      )}

      {/* Small toast while a connection test runs */}
      <AnimatePresence>
        {testingConn && (
          <motion.div className="sv-toast" initial={{ opacity: 0, y: 16, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: 10, x: '-50%' }} transition={{ duration: 0.18 }}>
            <span className="sv-spin" />
            {String(testingConn).includes('rcon') ? t('serversModal.testingRcon', 'Checking RCON…') : t('serversModal.testingConnection', 'Checking connection…')}
          </motion.div>
        )}
      </AnimatePresence>

      {locationFor && (
        <LocationModal server={locationFor} isLan={isLanServer(locationFor) || !!locationFor.lan} onClose={() => setLocationFor(null)}
          onSaved={(msg) => { setLocationFor(null); alert(msg, 'success'); fetchServers(); }} />
      )}
      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal(a => ({ ...a, show: false }))} />
      <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal(c => ({ ...c, show: false }))} />
    </div>
  );
}

export default Servers;
